import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { API_URL } from 'shared-library';
import { PortalProfile, PortalProfileInput } from '../models/profile.model';

/** The signed-in investigator's profile and photo, shared by the shell bar avatar and the settings page. */
@Injectable({ providedIn: 'root' })
export class PortalProfileService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(API_URL)}investigator-portal/profile`;

  readonly profile = signal<PortalProfile | null>(null);
  /** Object URL of the photo (the endpoint needs the access token, so the image cannot be linked directly). */
  readonly photoUrl = signal<string | null>(null);

  load(): void {
    this.http.get<PortalProfile>(this.baseUrl).subscribe({
      next: (profile) => {
        this.profile.set(profile);
        if (profile.photoFilePath) this.loadPhoto();
      },
      error: () => undefined, // the shell falls back to the data of the sign-in
    });
  }

  save(input: PortalProfileInput): Observable<PortalProfile> {
    return this.http.put<PortalProfile>(this.baseUrl, input).pipe(tap((profile) => this.profile.set(profile)));
  }

  /** PUT .../profile/password: 400 with a message when the current password is wrong or the new one breaks the rules. */
  changePassword(password: string, newPassword: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/password`, { password, newPassword });
  }

  uploadPhoto(file: File): Observable<void> {
    const form = new FormData();
    form.append('File', file, file.name);
    return this.http.put<void>(`${this.baseUrl}/photo`, form).pipe(tap(() => this.loadPhoto()));
  }

  private loadPhoto(): void {
    this.http.get(`${this.baseUrl}/photo`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const previous = this.photoUrl();
        this.photoUrl.set(URL.createObjectURL(blob));
        if (previous) URL.revokeObjectURL(previous);
      },
      error: () => this.photoUrl.set(null),
    });
  }
}
